import { Shield, AlertCircle } from 'react-feather';
import { useState, useEffect, useCallback } from 'react';
import Modal from '../Modal';
import { API_BASE_URL } from '../../config/api';

/* Two-factor authentication, on its own card.

   Previously this shared a card with "change password" under a combined
   "Authentication" heading. They answer different questions, one is a
   one-line on/off decision, the other is a three-field form, and pairing
   them meant the switch sat above fields it has nothing to do with, with a
   single "Change password" footer button that visually claimed both.

   The password is required in both directions and the backend enforces it:
   without it, a stolen session token could switch off the control that
   exists to contain exactly that situation. The dialog is where the user
   supplies it, so confirming never grows the card.

   The setting is organisation-wide: an admin turning it on enrols every
   member, and any admin of the same organisation can turn it back off.
   Members see the resulting state but get no switch. `can_manage` comes from
   the server rather than being read off the local role, so the UI and the
   endpoint's own 403 always agree, the switch is hidden for the same reason
   the request would be refused, not merely alongside it. */

const TwoFactorSection = () => {
  const [fetching, setFetching] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [orgWide, setOrgWide] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  // Which state the user is moving to; null means no change is pending.
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loadFailed, setLoadFailed] = useState(false);

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('access_token')}`
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/mfa/settings`, {
          headers: authHeaders()
        });
        if (!response.ok) throw new Error('Failed to load');

        const data = await response.json();
        if (cancelled) return;

        setEnabled(Boolean(data.mfa_enabled));
        setCanManage(Boolean(data.can_manage));
        setOrgWide(Boolean(data.organization_wide));
        setEmail(data.email || '');
      } catch (error) {
        // Surfaced as the panel's own line of text rather than a banner, so a
        // failure does not change the card's height.
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authHeaders]);

  // The toggle only opens the confirmation; nothing changes until the password
  // is submitted, so a stray click cannot turn the factor off.
  const handleToggle = () => {
    if (loading || fetching || !canManage) return;
    setMessage({ type: '', text: '' });
    setPassword('');
    setRevealed(false);
    setPending(pending === null ? !enabled : null);
  };

  const cancelChange = () => {
    setPending(null);
    setPassword('');
    setRevealed(false);
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (pending === null || password.length === 0 || loading) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/mfa/settings`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          enabled: pending,
          current_password: password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // No success banner: the switch position and the panel's own line of
        // text already report the new state, so a message would restate it.
        setEnabled(Boolean(data.mfa_enabled));
        setPending(null);
        setPassword('');
        setRevealed(false);
        setMessage({ type: '', text: '' });
      } else {
        setMessage({
          type: 'error',
          text:
            data.detail ||
            data.error?.message ||
            'Could not update two-factor authentication. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An error occurred while updating your settings. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const turningOn = pending === true;

  const describeState = () => {
    if (!canManage) {
      return enabled
        ? `Your organization requires a code sent to ${email || 'your email'} each time you sign in. Only an admin can change this.`
        : 'Your organization does not require a second step when signing in. Only an admin can change this.';
    }
    if (enabled) {
      return orgWide
        ? 'Everyone in your organization enters a code from their email each time they sign in.'
        : `Codes are sent to ${email || 'your email'} each time you sign in.`;
    }
    return orgWide
      ? 'Add a code from your email on top of your password, for everyone in your organization.'
      : 'Add a code from your email on top of your password.';
  };

  const statusText = loadFailed
    ? 'Could not load this setting. Refresh to try again.'
    : describeState();

  return (
    <section className="account-section-card account-card-compact">
      <div className="account-section-header">
        <span className="account-section-icon" aria-hidden="true">
          <Shield />
        </span>
        <div className="account-section-heading">
          <h2 className="account-section-title">Two-factor authentication</h2>
          <p className="account-section-description">
            {canManage && orgWide
              ? 'Require a second step when your team signs in.'
              : 'Require a second step when signing in.'}
          </p>
        </div>
      </div>

      <div className="account-section-body">
        <div className={`account-callout${enabled ? ' is-active' : ''}`}>
          <span className="account-callout-icon" aria-hidden="true">
            <Shield />
          </span>
          <div className="account-callout-copy">
            <h3 className="account-callout-title" id="mfa-toggle-label">
              Email verification codes
            </h3>
            <p className="account-callout-text">{statusText}</p>
          </div>
          {canManage && !loadFailed && (
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-labelledby="mfa-toggle-label"
              onClick={handleToggle}
              disabled={loading || fetching}
              className={`toggle-button${enabled ? ' active' : ''}`}
            >
              <div className="toggle-slider" />
            </button>
          )}
        </div>
      </div>

      <Modal
        isOpen={pending !== null}
        onClose={cancelChange}
        title={
          turningOn
            ? 'Turn on two-factor authentication'
            : 'Turn off two-factor authentication'
        }
      >
        <form onSubmit={handleSubmit} noValidate>
          <p className="account-modal-text">
            {orgWide
              ? turningOn
                ? 'Everyone in your organization will enter a 6-digit code sent to their email every time they sign in.'
                : 'Every account in your organization will be protected by a password alone. Another admin can turn this back on at any time.'
              : turningOn
                ? `You'll enter a 6-digit code sent to ${email || 'your email'} every time you sign in.`
                : 'Your account will be protected by your password alone.'}
          </p>

          {message.type === 'error' && message.text && (
            <div className="account-banner error" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>{message.text}</span>
            </div>
          )}

          <div className="account-field">
            <label htmlFor="mfa-password">Confirm your password</label>
            <div className="account-input-affix">
              <input
                id="mfa-password"
                type={revealed ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setMessage({ type: '', text: '' });
                }}
                autoComplete="current-password"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                required
              />
              <button
                type="button"
                className="account-reveal-button"
                onClick={() => setRevealed((v) => !v)}
                aria-label={`${revealed ? 'Hide' : 'Show'} password`}
              >
                {revealed ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="account-modal-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={cancelChange}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={password.length === 0 || loading}
            >
              {loading && <span className="account-spinner" aria-hidden="true" />}
              {loading ? 'Saving…' : turningOn ? 'Turn on' : 'Turn off'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export default TwoFactorSection;
